// components/features/support/tickets/TicketForm.tsx
import { useState, useEffect } from 'react';
import { Ticket } from '../../../../types/features/support/Ticket';
import useClients from '../../../../hooks/support/clients/useClients';

interface TicketFormProps {
  initialData?: Ticket;
  onSubmit: (data: Omit<Ticket, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  isLoading?: boolean;
}

export default function TicketForm({ initialData, onSubmit, isLoading = false }: TicketFormProps) {
  const { clients } = useClients();
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const [customSlug, setCustomSlug] = useState('');
  const [customName, setCustomName] = useState('');

  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    description: '',
    solution: '',
    priority: 'moyenne' as 'basse' | 'moyenne' | 'haute' | 'critique',
    status: 'nouveau' as 'nouveau' | 'en_cours' | 'en_attente' | 'escalade' | 'resolu' | 'ferme',
    source: 'formulaire_web' as 'appel' | 'formulaire_web' | 'email',
    agent_id: null as string | null,
    resolved_at: null as string | null
  });

  // Liste des catégories avec name et slug
  const problemCategoriesList = [
    { name: 'Inscription', slug: 'inscription' },
    { name: 'Connexion', slug: 'connexion' },
    { name: 'OTP', slug: 'otp' },
    { name: 'KYC', slug: 'kyc' },
    { name: 'Wallet', slug: 'wallet' },
    { name: 'Transactions', slug: 'transactions' },
    { name: 'Retrait', slug: 'retrait' },
    { name: 'Dépôt', slug: 'depot' },
    { name: 'Buy & Sell', slug: 'buy-sell' },
    { name: 'Transfert bancaire', slug: 'transfert-bancaire' },
    { name: 'Sécurité', slug: 'securite' },
    { name: 'Technique', slug: 'technique' },
    { name: 'Notifications', slug: 'notifications' },
    { name: 'Parrainage', slug: 'parrainage' },
    { name: 'Autre', slug: 'autre' }
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        client_id: initialData.client_id!,
        title: initialData.title,
        description: initialData.description,
        solution: initialData.solution || '',
        priority: initialData.priority,
        status: initialData.status,
        source: initialData.source,
        agent_id: initialData.agent_id,
        resolved_at: initialData.resolved_at
      });
      setSelectedSlugs(initialData.problem_categories || []);
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      ...formData,
      problem_categories: selectedSlugs
    });
  };

  const toggleCategory = (slug: string) => {
    if (selectedSlugs.includes(slug)) {
      setSelectedSlugs(selectedSlugs.filter(s => s !== slug));
    } else {
      setSelectedSlugs([...selectedSlugs, slug]);
    }
  };

  const addCustomCategory = () => {
    if (customName.trim() && customSlug.trim()) {
      const slug = customSlug.trim().toLowerCase().replace(/\s+/g, '-');
      if (!selectedSlugs.includes(slug)) {
        setSelectedSlugs([...selectedSlugs, slug]);
      }
      setCustomName('');
      setCustomSlug('');
    }
  };

  const removeCategory = (slug: string) => {
    setSelectedSlugs(selectedSlugs.filter(s => s !== slug));
  };

  // Récupérer le nom d'une catégorie à partir de son slug
  const getCategoryName = (slug: string) => {
    const cat = problemCategoriesList.find(c => c.slug === slug);
    return cat ? cat.name : slug;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Client */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Client *
        </label>
        <select
          name="client_id"
          value={formData.client_id}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="">Sélectionner un client</option>
          {clients.map(client => (
            <option key={client.id} value={client.id}>
              {client.firstname} {client.lastname} - {client.phone || client.email}
            </option>
          ))}
        </select>
      </div>

      {/* Titre */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Titre *
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder="Ex: Paiement bloqué"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description *
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          rows={5}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          placeholder="Décrivez le problème..."
        />
      </div>

      {/* Catégories du problème */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Catégories du problème
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {problemCategoriesList.map(cat => (
            <button
              key={cat.slug}
              type="button"
              onClick={() => toggleCategory(cat.slug)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                selectedSlugs.includes(cat.slug)
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
        
        {/* Ajouter une catégorie personnalisée */}
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Nom de la catégorie"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
          <input
            type="text"
            value={customSlug}
            onChange={(e) => setCustomSlug(e.target.value)}
            placeholder="slug (sans espaces)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          />
          <button
            type="button"
            onClick={addCustomCategory}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Ajouter
          </button>
        </div>
        
        {/* Catégories sélectionnées */}
        {selectedSlugs.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedSlugs.map(slug => (
              <span
                key={slug}
                className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 text-blue-700 rounded-full"
              >
                {getCategoryName(slug)}
                <button
                  type="button"
                  onClick={() => removeCategory(slug)}
                  className="text-blue-500 hover:text-blue-700"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Solution */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Solution (optionnel)
        </label>
        <textarea
          name="solution"
          value={formData.solution}
          onChange={handleChange}
          rows={3}
          placeholder="Solution apportée au problème..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
        />
      </div>

      {/* Priorité et Source */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priorité
          </label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="basse">Basse</option>
            <option value="moyenne">Moyenne</option>
            <option value="haute">Haute</option>
            <option value="critique">Critique</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Source
          </label>
          <select
            name="source"
            value={formData.source}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="appel">Appel</option>
            <option value="formulaire_web">Formulaire web</option>
            <option value="email">Email</option>
          </select>
        </div>
      </div>

      {/* Boutons */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {isLoading ? 'Création...' : initialData ? 'Modifier' : 'Créer le ticket'}
        </button>
      </div>
    </form>
  );
}